'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { ControlBarLoginPopover } from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarActionDialog';
import ControlBarActionTooltip from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarActionTooltip';
import ControlBarMoreButton from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarMoreButton';
import ControlBarRecommendationOverlay from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarRecommendationOverlay';
import LikeButton from '@/app/[locale]/(worlds)/[roomId]/components/LikeButton';
import SaveButton from '@/app/[locale]/(worlds)/[roomId]/components/SaveButton';
import WorldReportModal from '@/app/[locale]/(worlds)/[roomId]/components/WorldReportModal';
import IconControlBarLoginLike from '@/components/assets/icon_control_bar_login_like.svg';
import IconControlBarLoginReport from '@/components/assets/icon_control_bar_login_report.svg';
import IconControlBarLoginSave from '@/components/assets/icon_control_bar_login_save.svg';
import ReportConfirmationModal from '@/components/ReportConfirmationModal';
import ViverseButton from '@/components/ViverseButton';
import type { WorldReportVariant } from '@/constants/report';
import { useAuthContext } from '@/context/AuthContext';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useWorldDetailsContext } from '@/context/WorldDetailsContext';
import { cn } from '@/lib/utils';
import roomDefaultImage from '@/public/images/room-default.png';
import type { RecommendRoom } from '@/services/cms';
import { CircleAlert } from 'lucide-react';
import { useIsClient } from 'usehooks-ts';
export type ControlBarLoginAction = 'like' | 'save';
type ControlBarMoreActionProps = {
  buttonClassName?: string;
  buttonWrapperClassName?: string;
  contentKind: 'experiences' | 'games';
  moreRecommendationsPromise?: Promise<RecommendRoom[]> | null;
  popupContainer?: HTMLDivElement | null;
  showIcon?: boolean;
};
export function ControlBarMoreAction(t0) {
  const $ = _c(23);
  const {
    buttonClassName,
    buttonWrapperClassName,
    contentKind,
    moreRecommendationsPromise,
    popupContainer,
    showIcon
  } = t0;
  const t = useTranslations();
  const {
    logoOverlayModel,
    requestControlBarMoreRecommendations
  } = useWorldDetailsContext();
  const [isOpen, setIsOpen] = useState(false);
  let t1;
  let t2;
  if ($[0] !== logoOverlayModel || $[1] !== requestControlBarMoreRecommendations) {
    t1 = () => {
      if (logoOverlayModel) {
        requestControlBarMoreRecommendations();
      }
    };
    t2 = [logoOverlayModel, requestControlBarMoreRecommendations];
    $[0] = logoOverlayModel;
    $[1] = requestControlBarMoreRecommendations;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setIsOpen(_temp);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleMoreClick = t3;
  if (!logoOverlayModel) {
    return null;
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("user-worlds.button.more");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== buttonClassName || $[8] !== contentKind || $[9] !== showIcon || $[10] !== t4) {
    t5 = <ControlBarMoreButton data-action="more" contentKind={contentKind} className={buttonClassName} onClick={handleMoreClick} showIcon={showIcon}>{t4}</ControlBarMoreButton>;
    $[7] = buttonClassName;
    $[8] = contentKind;
    $[9] = showIcon;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== buttonWrapperClassName || $[13] !== t5) {
    t6 = <span className={buttonWrapperClassName}>{t5}</span>;
    $[12] = buttonWrapperClassName;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== isOpen || $[16] !== logoOverlayModel || $[17] !== moreRecommendationsPromise || $[18] !== popupContainer) {
    t7 = isOpen && popupContainer && <ControlBarRecommendationOverlay model={logoOverlayModel} open={isOpen} portalContainer={popupContainer} roomsPromise={moreRecommendationsPromise ?? null} onCloseAction={() => setIsOpen(false)} />;
    $[15] = isOpen;
    $[16] = logoOverlayModel;
    $[17] = moreRecommendationsPromise;
    $[18] = popupContainer;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t6 || $[21] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}
function _temp(currentOpen) {
  return !currentOpen;
}
type ControlBarLikeActionProps = {
  className: string;
  isLoginPopoverOpen: boolean;
  onLogin: () => void;
  onLoginPopoverOpenChange: (open: boolean) => void;
  onSignedOutClick: () => void;
  popupContainer?: HTMLDivElement | null;
};
export function ControlBarLikeAction(t0) {
  const $ = _c(30);
  const {
    className,
    isLoginPopoverOpen,
    onLogin,
    onLoginPopoverOpenChange,
    onSignedOutClick,
    popupContainer
  } = t0;
  const t = useTranslations();
  const {
    roomData
  } = useCheckEnterRoomContext();
  let t1;
  if ($[0] !== t) {
    t1 = t("action-list.like");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== className || $[3] !== onSignedOutClick) {
    t2 = <LikeButton className={className} variant="controlBar" onSignedOutClick={onSignedOutClick} />;
    $[2] = className;
    $[3] = onSignedOutClick;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <IconControlBarLoginLike className="size-5" aria-hidden="true" />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("media-preview-modal.close");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = t("control-bar.login-dialog.like.description");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = roomData.title;
  const t7 = roomData.image || roomDefaultImage.src;
  let t8;
  if ($[10] !== t) {
    t8 = t("control-bar.login-dialog.action");
    $[10] = t;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t) {
    t9 = t("control-bar.login-dialog.like.title");
    $[12] = t;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== isLoginPopoverOpen || $[15] !== onLogin || $[16] !== onLoginPopoverOpenChange || $[17] !== popupContainer || $[18] !== roomData.title || $[19] !== t2 || $[20] !== t4 || $[21] !== t5 || $[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
    t10 = <ControlBarLoginPopover open={isLoginPopoverOpen} onOpenChange={onLoginPopoverOpenChange} trigger={t2} portalContainer={popupContainer} badge={t3} badgeClassName="bg-control-bar-action-like" closeLabel={t4} description={t5} imageAlt={t6} imageSrc={t7} loginLabel={t8} onLogin={onLogin} title={t9} />;
    $[14] = isLoginPopoverOpen;
    $[15] = onLogin;
    $[16] = onLoginPopoverOpenChange;
    $[17] = popupContainer;
    $[18] = roomData.title;
    $[19] = t2;
    $[20] = t4;
    $[21] = t5;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== popupContainer || $[27] !== t1 || $[28] !== t10) {
    t11 = <ControlBarActionTooltip label={t1} portalContainer={popupContainer}>{t10}</ControlBarActionTooltip>;
    $[26] = popupContainer;
    $[27] = t1;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
}
type ControlBarSaveActionProps = {
  className: string;
  isLoginPopoverOpen: boolean;
  onLogin: () => void;
  onLoginPopoverOpenChange: (open: boolean) => void;
  onSignedOutClick: () => void;
  popupContainer?: HTMLDivElement | null;
};
export function ControlBarSaveAction(t0) {
  const $ = _c(30);
  const {
    className,
    isLoginPopoverOpen,
    onLogin,
    onLoginPopoverOpenChange,
    onSignedOutClick,
    popupContainer
  } = t0;
  const t = useTranslations();
  const {
    roomData
  } = useCheckEnterRoomContext();
  let t1;
  if ($[0] !== t) {
    t1 = t("worlds-landing.saved.aria.save");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== className || $[3] !== onSignedOutClick) {
    t2 = <SaveButton className={className} iconClassName="size-4 @[954px]:size-5" onSignedOutClick={onSignedOutClick} />;
    $[2] = className;
    $[3] = onSignedOutClick;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <IconControlBarLoginSave className="size-5" aria-hidden="true" />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("media-preview-modal.close");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = t("control-bar.login-dialog.save.description");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = roomData.title;
  const t7 = roomData.image || roomDefaultImage.src;
  let t8;
  if ($[10] !== t) {
    t8 = t("control-bar.login-dialog.action");
    $[10] = t;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t) {
    t9 = t("control-bar.login-dialog.save.title");
    $[12] = t;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== isLoginPopoverOpen || $[15] !== onLogin || $[16] !== onLoginPopoverOpenChange || $[17] !== popupContainer || $[18] !== roomData.title || $[19] !== t2 || $[20] !== t4 || $[21] !== t5 || $[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
    t10 = <ControlBarLoginPopover open={isLoginPopoverOpen} onOpenChange={onLoginPopoverOpenChange} trigger={t2} portalContainer={popupContainer} badge={t3} badgeClassName="bg-control-bar-action-save" closeLabel={t4} description={t5} imageAlt={t6} imageSrc={t7} loginLabel={t8} onLogin={onLogin} title={t9} />;
    $[14] = isLoginPopoverOpen;
    $[15] = onLogin;
    $[16] = onLoginPopoverOpenChange;
    $[17] = popupContainer;
    $[18] = roomData.title;
    $[19] = t2;
    $[20] = t4;
    $[21] = t5;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== popupContainer || $[27] !== t1 || $[28] !== t10) {
    t11 = <ControlBarActionTooltip label={t1} portalContainer={popupContainer}>{t10}</ControlBarActionTooltip>;
    $[26] = popupContainer;
    $[27] = t1;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
}
type ControlBarReportActionProps = {
  className: string;
  iconClassName?: string;
  popupContainer?: HTMLDivElement | null;
  reportModalContainer?: HTMLDivElement | null;
  reportVariant?: WorldReportVariant;
};
export function ControlBarReportAction(t0) {
  const $ = _c(62);
  const {
    className,
    iconClassName,
    popupContainer,
    reportModalContainer,
    reportVariant
  } = t0;
  const t = useTranslations();
  const {
    handleLogin,
    isSignedIn
  } = useAuthContext();
  const {
    store
  } = useConfigsContext();
  const {
    canPreview,
    isCoOwner,
    isOwner,
    roomData
  } = useCheckEnterRoomContext();
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isReportSuccessOpen, setIsReportSuccessOpen] = useState(false);
  const [isLoginPopoverOpen, setIsLoginPopoverOpen] = useState(false);
  const isClient = useIsClient();
  const canReport = isClient && !!store?.credentials?.htcToken && !!roomData.id;
  const isReportPending = isSignedIn && !canReport;
  if (isOwner || isCoOwner || canPreview) {
    return null;
  }
  let t1;
  if ($[0] !== t) {
    t1 = t("world-more-action.report");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== className) {
    t2 = cn(className, "disabled:opacity-40");
    $[2] = className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("world-more-action.report");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== isReportPending || $[7] !== isSignedIn) {
    t4 = () => {
      if (!isSignedIn) {
        setIsLoginPopoverOpen(_temp2);
        return;
      }
      if (isReportPending) {
        return;
      }
      setIsReportModalOpen(true);
    };
    $[6] = isReportPending;
    $[7] = isSignedIn;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = iconClassName ?? "size-4 shrink-0 @[954px]:size-5";
  let t6;
  if ($[9] !== t5) {
    t6 = <CircleAlert className={t5} aria-hidden="true" />;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t) {
    t7 = t("world-more-action.report");
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <span className="hidden text-sm leading-[22px] font-semibold @[954px]:inline">{t7}</span>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== isReportPending || $[16] !== t2 || $[17] !== t3 || $[18] !== t4 || $[19] !== t6 || $[20] !== t8) {
    t9 = <ViverseButton data-action="report" className={t2} variant="filter" size="custom" disabled={isReportPending} aria-label={t3} onClick={t4}>{t6}{t8}</ViverseButton>;
    $[15] = isReportPending;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t6;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <IconControlBarLoginReport className="size-5" aria-hidden="true" />;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t) {
    t11 = t("media-preview-modal.close");
    $[23] = t;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t) {
    t12 = t("control-bar.login-dialog.report.description");
    $[25] = t;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = roomData.title;
  const t14 = roomData.image || roomDefaultImage.src;
  let t15;
  if ($[27] !== t) {
    t15 = t("control-bar.login-dialog.action");
    $[27] = t;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== handleLogin) {
    t16 = () => {
      handleLogin({
        callback: window.location.href
      });
      setIsLoginPopoverOpen(false);
    };
    $[29] = handleLogin;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t) {
    t17 = t("control-bar.login-dialog.report.title");
    $[31] = t;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== isLoginPopoverOpen || $[34] !== popupContainer || $[35] !== roomData.title || $[36] !== t11 || $[37] !== t12 || $[38] !== t14 || $[39] !== t15 || $[40] !== t16 || $[41] !== t17 || $[42] !== t9) {
    t18 = <ControlBarLoginPopover open={isLoginPopoverOpen} onOpenChange={setIsLoginPopoverOpen} trigger={t9} portalContainer={popupContainer} badge={t10} badgeClassName="bg-control-bar-action-report" closeLabel={t11} description={t12} imageAlt={t13} imageSrc={t14} loginLabel={t15} onLogin={t16} title={t17} />;
    $[33] = isLoginPopoverOpen;
    $[34] = popupContainer;
    $[35] = roomData.title;
    $[36] = t11;
    $[37] = t12;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
    $[41] = t17;
    $[42] = t9;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== popupContainer || $[45] !== t1 || $[46] !== t18) {
    t19 = <ControlBarActionTooltip label={t1} portalContainer={popupContainer}>{t18}</ControlBarActionTooltip>;
    $[44] = popupContainer;
    $[45] = t1;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  let t21;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setIsReportModalOpen(false);
    t21 = () => setIsReportSuccessOpen(true);
    $[48] = t20;
    $[49] = t21;
  } else {
    t20 = $[48];
    t21 = $[49];
  }
  let t22;
  if ($[50] !== isReportModalOpen || $[51] !== reportModalContainer || $[52] !== reportVariant) {
    t22 = <WorldReportModal container={reportModalContainer} isDialogOpen={isReportModalOpen} onClose={t20} onReportSuccess={t21} variant={reportVariant} />;
    $[50] = isReportModalOpen;
    $[51] = reportModalContainer;
    $[52] = reportVariant;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = () => setIsReportSuccessOpen(false);
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== isReportSuccessOpen || $[56] !== reportModalContainer) {
    t24 = <ReportConfirmationModal container={reportModalContainer} open={isReportSuccessOpen} onAction={t23} />;
    $[55] = isReportSuccessOpen;
    $[56] = reportModalContainer;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== t19 || $[59] !== t22 || $[60] !== t24) {
    t25 = <>{t19}{t22}{t24}</>;
    $[58] = t19;
    $[59] = t22;
    $[60] = t24;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  return t25;
}
function _temp2(currentOpen) {
  return !currentOpen;
}